import { ArrowLeftIcon } from "@phosphor-icons/react/dist/csr/ArrowLeft";
import { CalculatorIcon } from "@phosphor-icons/react/dist/csr/Calculator";
import { ClockCounterClockwiseIcon } from "@phosphor-icons/react/dist/csr/ClockCounterClockwise";
import { GearSixIcon } from "@phosphor-icons/react/dist/csr/GearSix";
import { ListChecksIcon } from "@phosphor-icons/react/dist/csr/ListChecks";
import { UsersIcon } from "@phosphor-icons/react/dist/csr/Users";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@volca/ui/components/ui/select";
import { Stack } from "@volca/ui/components/ui/stack";
import { Heading, Text } from "@volca/ui/components/ui/text";
import { ShellColumn } from "@/app-shell";
import { PanelRow } from "@/app-shell/sidebar-panel-row";
import {
  profitabilityTemplatePath,
  profitabilityTemplateSettingsPath,
} from "@/profitability/routes";

export const settingsPages = [
  { id: "general", label: "General", icon: GearSixIcon },
  { id: "calculations", label: "Calculations", icon: CalculatorIcon },
  { id: "rules", label: "Review rules", icon: ListChecksIcon },
  { id: "people", label: "People", icon: UsersIcon },
  {
    id: "versions",
    label: "Versions",
    icon: ClockCounterClockwiseIcon,
    manages: true,
  },
] as const;
export type SettingsPage = (typeof settingsPages)[number]["id"];
export const settingsPath = (reportId: string, page: string) =>
  profitabilityTemplateSettingsPath(
    reportId,
    page === "general" ? undefined : page,
  );
export const visibleSettingsPages = (manages: boolean) =>
  settingsPages.filter((page) => !("manages" in page) || manages);
export const legacySettingsPage: Record<string, SettingsPage> = {
  overview: "general",
  period: "general",
  rates: "calculations",
  matching: "general",
  sharing: "people",
  history: "versions",
  archive: "general",
};

export const SettingsNav = ({
  reportId,
  name,
  page,
  manages,
  docked,
}: {
  reportId: string;
  name: string;
  page: SettingsPage;
  manages: boolean;
  docked: boolean;
}) => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const period = params.get("period");
  const withPeriod = (path: string) =>
    period ? `${path}?${new URLSearchParams({ period })}` : path;
  const pages = visibleSettingsPages(manages);
  if (!docked)
    return (
      <Stack gap={3}>
        <Link
          to={withPeriod(profitabilityTemplatePath(reportId))}
          className="flex min-h-9 w-fit items-center gap-2 text-sm text-subtle hover:text-ink focus-visible:outline-2 focus-visible:outline-ink pointer-coarse:min-h-11"
        >
          <ArrowLeftIcon aria-hidden className="rtl:rotate-180" />
          Back to agent
        </Link>
        <Select
          value={page}
          items={Object.fromEntries(pages.map((item) => [item.id, item.label]))}
          onValueChange={(value) =>
            value && navigate(withPeriod(settingsPath(reportId, value)))
          }
        >
          <SelectTrigger
            aria-label="Settings section"
            className="w-full sm:w-60"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="pointer-coarse:[&_[data-slot=select-item]]:min-h-11">
            {pages.map((item) => (
              <SelectItem key={item.id} value={item.id}>
                {item.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Stack>
    );
  return (
    <ShellColumn open label="Agent settings" width={240}>
      <Stack className="min-h-0 flex-1">
        <Stack direction="row" align="center" className="h-10 shrink-0 px-3">
          <Heading level={2} variant="label" tone="muted">
            Settings
          </Heading>
        </Stack>
        <Stack
          gap={4}
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-2 py-1"
        >
          <ul>
            <PanelRow
              row={{
                label: "Back to agent",
                to: withPeriod(profitabilityTemplatePath(reportId)),
                icon: ArrowLeftIcon,
                isActive: false,
              }}
            />
          </ul>
          <Stack render={<nav aria-label="Settings sections" />} gap={1}>
            <Text
              variant="caption"
              tone="muted"
              className="px-2 pb-1 [overflow-wrap:anywhere]"
            >
              {name}
            </Text>
            <ul>
              {pages.map((item) => (
                <PanelRow
                  key={item.id}
                  row={{
                    label: item.label,
                    to: withPeriod(settingsPath(reportId, item.id)),
                    icon: item.icon,
                    isActive: page === item.id,
                  }}
                />
              ))}
            </ul>
          </Stack>
        </Stack>
      </Stack>
    </ShellColumn>
  );
};
